import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateNodeData } from '../../../../store/workflow/workflowSlice';
import type { RootState } from '../../../../store';
import { resolveEndpoint, assumedSamColumns, nodeColumns, producedColumns, kindLabelOf } from '../../utils/transformLoad';
import type { Column, EndpointKind } from '../../utils/transformLoad';
import { generateCompareRules, validateAiRules, CompareRule, CompareKey, findPath } from '../../utils/compareRules';
import { fetchCsvColumns } from '../../../../utils/csvColumnsService';
import { useAssistantEnabled } from '../../../../hooks/useAssistantEnabled';
import * as assistant from '../../../../services/assistantClient';
import { logUsage } from '../../../../services/eventLog';
import { typeLabelOf } from '../../nodeCatalog';
import { ColSelectBase, Section, inp } from './AnalyticsNodePanel';

/**
 * 데이터비교 노드 속성 (2026-10-20 사용자 정의).
 *
 * 비교 대상 둘(점선으로 이은 데이터 노드) · 각 쪽 범위/필터 · 키 · 비교 로직(항목 단위) · 실행 시점 · 비교 방식 · 레포트.
 * 비교 로직은 「규칙으로 생성」(흐름의 매핑을 따라 — utils/compareRules) 또는 「✨ AI 보조」 로 채우고, 사람이 확인·편집·제외한다.
 * AI 가 없어도 끝까지 쓸 수 있다(직접 입력·규칙 생성). AI 제안은 기계로 검증하고, 버린 것은 사유와 함께 보인다.
 */
interface Props { node: any }

const DATA = new Set(['csvData', 'rdbData', 'innerData']);
const kindOf = (n: any): EndpointKind => (n?.type === 'csvData' ? 'sam' : n?.type === 'rdbData' ? 'db' : 'inner');
const smallBtn = 'px-2 py-0.5 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50';

const CompareNodePanel: React.FC<Props> = ({ node }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nodes = useSelector((s: any) => s.workflow.nodes);
  const edges = useSelector((s: any) => s.workflow.edges);
  const connections = useSelector((s: RootState) => s.connections.list);
  const assistantOn = useAssistantEnabled();
  const cfg = node?.data?.config || {};
  const patch = useCallback((p: Record<string, any>) => {
    dispatch(updateNodeData({ id: node.id, data: { config: { ...cfg, ...p }, ...(p.label !== undefined ? { label: p.label } : {}) } } as any));
  }, [cfg, dispatch, node.id]);

  // ── 비교 대상: 이 노드로 이은 데이터 노드. 속성에서 정한 원천·타겟이 있으면 그것, 아니면 이은 순서
  const inputs = useMemo(() => edges.filter((e: any) => e.target === node.id)
    .map((e: any) => nodes.find((n: any) => n.id === e.source)).filter((n: any) => n && DATA.has(n.type)), [edges, nodes, node.id]);
  const pick = (id?: string) => inputs.find((n: any) => n.id === id);
  const srcNode = pick(cfg.sourceNodeId) || inputs[0];
  const tgtNode = (pick(cfg.targetNodeId) && cfg.targetNodeId !== srcNode?.id ? pick(cfg.targetNodeId) : inputs.find((n: any) => n.id !== srcNode?.id));
  const report = useMemo(() => resolveEndpoint(node.id, 'down', nodes, edges), [node.id, nodes, edges]);

  // ── 항목: 노드에 있으면 그것, SAM 은 서버에서 읽고, 아직 없는 파일은 그 파일을 만드는 처리의 항목으로 가정
  const [samCols, setSamCols] = useState<Record<string, { columns?: Column[]; error?: string; loading?: boolean }>>({});
  const samKey = (n: any) => {
    if (n?.type !== 'csvData') return '';
    const c = n.data?.config || {};
    return `${c.filepath}|${c.delimiter || ','}|${c.hasHeader !== false}|${c.encoding || 'UTF-8'}`;
  };
  const loadSam = useCallback((n: any) => {
    const key = samKey(n);
    if (!key || samCols[key]) return;                    // 결과·오류가 있으면 다시 하지 않는다(끝없이 도는 것 방지)
    const c = n.data?.config || {};
    if (!c.filepath) { setSamCols((m) => ({ ...m, [key]: { error: t('property:sam.noPath') } })); return; }
    setSamCols((m) => ({ ...m, [key]: { loading: true } }));
    fetchCsvColumns(c.filepath, c.delimiter || ',', c.hasHeader !== false, c.encoding || 'UTF-8')
      .then((r) => setSamCols((m) => ({ ...m, [key]: { columns: r.columns } })))
      .catch((e) => setSamCols((m) => ({ ...m, [key]: { error: t('property:sam.columnsFailed', { message: e.message }) } })));
  }, [samCols, t]);
  useEffect(() => { loadSam(srcNode); loadSam(tgtNode); }, [srcNode, tgtNode, loadSam]);
  const colsOf = (n: any): Column[] => {
    if (!n) return [];
    if (n.type === 'csvData') {
      const st = samCols[samKey(n)];
      if (st?.columns?.length) return st.columns;
      const own = nodeColumns(n);
      return own.length ? own : assumedSamColumns(n.id, nodes, edges);
    }
    if (n.type === 'innerData') {
      const up = resolveEndpoint(n.id, 'up', nodes, edges);
      return up ? producedColumns(up.node.id, nodes, edges) : [];
    }
    return nodeColumns(n);
  };
  const srcCols = colsOf(srcNode);
  const tgtCols = colsOf(tgtNode);
  const srcNames = srcCols.map((c) => c.name);
  const tgtNames = tgtCols.map((c) => c.name);
  const keys: CompareKey[] = cfg.keys || [];
  const rules: CompareRule[] = cfg.rules || [];
  const setRule = (i: number, p: Partial<CompareRule>) =>
    // 켜고 끄기만 하면 출처를 그대로 두고(AI 제안을 켠 것은 여전히 AI), 내용을 고치면 「직접」 이 된다
    patch({ rules: rules.map((r, k) => (k === i ? { ...r, ...p, origin: Object.keys(p).length === 1 && 'enabled' in p ? r.origin : 'user' } : r)) });

  const [notes, setNotes] = useState<string[]>([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMsg, setAiMsg] = useState<{ dropped: string[]; notes: string; cost: number; error?: string } | null>(null);
  const [instruction, setInstruction] = useState('');

  const generate = () => {
    if (!srcNode || !tgtNode) return;
    const r = generateCompareRules(srcNode.id, tgtNode.id, nodes, edges, srcCols, tgtCols, keys);
    // 사람이 고친 규칙(origin user)은 지키고, 나머지는 새로 만든 것으로 바꾼다
    const mine = rules.filter((x) => x.origin === 'user');
    const fresh = r.rules.filter((x) => !mine.some((m) => m.target.toUpperCase() === x.target.toUpperCase()));
    patch({ rules: [...mine, ...fresh], ...(keys.length ? {} : { keys: r.keys }) });
    setNotes(r.notes);
    logUsage('compare.generate', { detail: { rules: fresh.length, kept: mine.length } });
  };

  /** AI 보조 — 항목 이름·타입과 흐름의 매핑(식)만 보낸다. 데이터 값(표본)은 보내지 않는다 */
  const askAi = async () => {
    if (!srcNode || !tgtNode) return;
    setAiBusy(true); setAiMsg(null);
    const path = findPath(srcNode.id, tgtNode.id, nodes, edges) || [];
    const lineage = path.slice(1, -1).map((id) => nodes.find((n: any) => n.id === id)).filter(Boolean).map((n: any) => ({
      type: n.type, label: n.data?.label || n.type,
      ...(n.type === 'transformLoad' ? { transformMode: n.data?.config?.transformMode, mapping: (n.data?.config?.mapping || []).map((m: any) => ({ target: m.target, source: m.source, expr: m.expr })) } : {}),
      ...(n.type === 'filter' ? { condition: n.data?.config?.condition } : {}),
    }));
    const pref = (k: string, d: string) => { try { return localStorage.getItem(k) || d; } catch { return d; } };
    try {
      const r = await assistant.runTask<{ rules: any[]; notes?: string }>('suggest_compare_rules', {
        sourceColumns: srcCols.map((c) => ({ name: c.name, dataType: c.dataType || '' })),
        targetColumns: tgtCols.map((c) => ({ name: c.name, dataType: c.dataType || '' })),
        keys, lineage,
        currentRules: rules.filter((x) => x.enabled !== false).map(({ target, source, expr }) => ({ target, source, expr })),
        instruction: instruction.trim(),
      }, { provider: pref('openflow.assistant.provider', 'claude'), model: pref('openflow.assistant.model', 'default') });
      const { kept, dropped } = validateAiRules(r.result.rules || [], srcNames, tgtNames);
      // AI 제안은 꺼 둔 채로 붙인다 — 사람이 확인하고 켠다. 같은 타겟의 기존 AI 제안은 새것으로 바꾼다
      const others = rules.filter((x) => !(x.origin === 'ai' && kept.some((k) => k.target.toUpperCase() === x.target.toUpperCase())));
      patch({ rules: [...others, ...kept] });
      setAiMsg({ dropped, notes: r.result.notes || '', cost: r.costUsd || 0 });
      logUsage('compare.ai', { detail: { kept: kept.length, dropped: dropped.length, costUsd: r.costUsd || 0 } });
    } catch (e: any) {
      setAiMsg({ dropped: [], notes: '', cost: 0, error: e?.message || t('property:cmp.aiFailed') });
    } finally {
      setAiBusy(false);
    }
  };

  const sideLabel = (n: any) => (n ? `${kindLabelOf(kindOf(n))} · ${n.data?.config?.filepath || n.data?.config?.table || n.data?.label || n.id}` : '');
  const filterHint = (n: any) => (n?.type === 'rdbData' ? t('property:cmp.filterHintDb') : t('property:cmp.filterHintSam'));
  const hasSam = [srcNode, tgtNode].some((n) => n?.type === 'csvData');

  return (
    <div className="h-full bg-white p-4 overflow-auto">
      <div className="mb-3 pb-3 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{t('property:ui.properties')}</h3>
        <div className="flex items-center gap-2"><span className="text-xs font-semibold text-gray-500">{t('property:an.nodeType')}</span>
          <input type="text" value={typeLabelOf('compare')} readOnly className="text-xs text-gray-700 bg-gray-100 border-none p-1 rounded w-auto" style={{ minWidth: 80 }} /></div>
      </div>
      <div className="mb-3"><label className="block text-xs font-semibold text-gray-500 mb-1">{t('workflow:field.label')}</label>
        <input className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={cfg.label ?? node.data?.label ?? ''} onChange={(e) => patch({ label: e.target.value })} /></div>

      <Section title={t('property:cmp.sides')} hint={t('property:cmp.sidesHint')}>
        {inputs.length < 2 ? (
          <div className="text-xs text-amber-600">{t('property:cmp.needTwo', { n: inputs.length })}</div>
        ) : (
          <>
            {(['source', 'target'] as const).map((side) => {
              const cur = side === 'source' ? srcNode : tgtNode;
              return (
                <div key={side} className="flex items-center gap-1 mb-1 text-xs">
                  <span className="w-10 shrink-0 text-gray-500">{t(`property:cmp.${side}`)}</span>
                  <select className={`${inp} flex-1`} value={cur?.id || ''} onChange={(e) => {
                    const other = side === 'source' ? tgtNode : srcNode;
                    const v = e.target.value;
                    const swapped = other?.id === v ? (side === 'source' ? srcNode?.id : tgtNode?.id) : other?.id;
                    patch(side === 'source' ? { sourceNodeId: v, targetNodeId: swapped } : { targetNodeId: v, sourceNodeId: swapped });
                  }}>
                    {inputs.map((n: any) => <option key={n.id} value={n.id}>{sideLabel(n)}</option>)}
                  </select>
                </div>
              );
            })}
            {inputs.length > 2 && <div className="text-[0.6875rem] text-amber-600">{t('property:cmp.tooMany', { n: inputs.length })}</div>}
          </>
        )}
      </Section>

      <Section title={t('property:cmp.filters')} hint={t('property:cmp.filtersHint')}>
        {(['source', 'target'] as const).map((side) => {
          const n = side === 'source' ? srcNode : tgtNode;
          const k = side === 'source' ? 'sourceFilter' : 'targetFilter';
          return (
            <div key={side} className="mb-1.5">
              <div className="text-[0.6875rem] text-gray-500 mb-0.5">{t(`property:cmp.${side}`)} — {filterHint(n)}</div>
              <textarea className={`${inp} w-full font-mono`} rows={2} value={cfg[k] || ''} placeholder={t('property:cmp.filterPlaceholder')}
                onChange={(e) => patch({ [k]: e.target.value })} />
            </div>
          );
        })}
      </Section>

      <Section title={t('property:cmp.keys')} hint={t('property:cmp.keysHint')}>
        {keys.map((k, i) => (
          <div key={i} className="flex items-center gap-1 mb-1">
            <div className="flex-1"><ColSelectBase cols={srcNames} value={k.source} placeholder={t('property:cmp.sourceCol')}
              onChange={(v) => patch({ keys: keys.map((x, j) => (j === i ? { ...x, source: v, target: x.target || v } : x)) })} /></div>
            <span className="text-gray-400">=</span>
            <div className="flex-1"><ColSelectBase cols={tgtNames} value={k.target} placeholder={t('property:cmp.targetCol')}
              onChange={(v) => patch({ keys: keys.map((x, j) => (j === i ? { ...x, target: v } : x)) })} /></div>
            <button className="text-gray-400 hover:text-red-500" onClick={() => patch({ keys: keys.filter((_, j) => j !== i) })}>✕</button>
          </div>
        ))}
        <button className={smallBtn} onClick={() => patch({ keys: [...keys, { source: '', target: '' }] })}>{t('property:cmp.addKey')}</button>
        {!keys.length && <span className="ml-2 text-[0.6875rem] text-amber-600">{t('property:cmp.noKey')}</span>}
      </Section>

      <Section title={t('property:cmp.rules')} hint={t('property:cmp.rulesHint')}>
        <div className="flex flex-wrap items-center gap-1 mb-1.5">
          <button className={smallBtn} disabled={!srcNode || !tgtNode} onClick={generate}>{t('property:cmp.generate')}</button>
          {assistantOn && (
            <button className="px-2 py-0.5 text-xs rounded border border-violet-400 text-violet-700 hover:bg-violet-50 disabled:opacity-50"
              disabled={aiBusy || !srcNode || !tgtNode} onClick={askAi} title={t('property:cmp.aiTitle')}>
              {aiBusy ? t('property:cmp.aiBusy') : t('property:cmp.ai')}
            </button>
          )}
          <button className={smallBtn} onClick={() => patch({ rules: [...rules, { target: '', source: '', trim: true, enabled: true, origin: 'user' }] })}>{t('property:cmp.addRule')}</button>
        </div>
        {assistantOn && (
          <input className={`${inp} w-full mb-1.5`} value={instruction} placeholder={t('property:cmp.aiInstruction')} onChange={(e) => setInstruction(e.target.value)} />
        )}
        {notes.length > 0 && <ul className="mb-1.5 list-disc pl-4 text-[0.6875rem] text-gray-500">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
        {aiMsg && (
          <div className="mb-1.5 text-[0.6875rem]">
            {aiMsg.error ? <div className="text-red-600">{aiMsg.error}</div> : (
              <>
                <div className="text-violet-700">{t('property:cmp.aiDone', { cost: aiMsg.cost.toFixed(4) })}</div>
                {aiMsg.notes && <div className="text-gray-500">{aiMsg.notes}</div>}
                {aiMsg.dropped.length > 0 && (
                  <div className="text-amber-700">{t('property:cmp.aiDropped', { n: aiMsg.dropped.length })}
                    <ul className="list-disc pl-4">{aiMsg.dropped.map((d, i) => <li key={i}>{d}</li>)}</ul></div>
                )}
              </>
            )}
          </div>
        )}
        {!rules.length && <div className="text-[0.6875rem] text-gray-400">{t('property:cmp.noRules')}</div>}
        {rules.map((r, i) => {
          const useExpr = r.expr !== undefined && r.expr !== null && (r.expr !== '' || !r.source);
          return (
            <div key={i} className={`mb-1 rounded border p-1.5 ${r.enabled === false ? 'border-gray-200 bg-gray-50 opacity-70' : 'border-gray-200'}`}>
              <div className="flex items-center gap-1">
                <input type="checkbox" checked={r.enabled !== false} onChange={(e) => setRule(i, { enabled: e.target.checked })} title={t('property:cmp.enabled')} />
                <div className="w-[38%]"><ColSelectBase cols={tgtNames} value={r.target} placeholder={t('property:cmp.targetCol')} onChange={(v) => setRule(i, { target: v })} /></div>
                <span className="text-gray-400">←</span>
                <div className="flex-1">
                  {useExpr
                    ? <input className={`${inp} w-full font-mono`} value={r.expr || ''} placeholder={t('property:cmp.exprPlaceholder')} onChange={(e) => setRule(i, { expr: e.target.value })} />
                    : <ColSelectBase cols={srcNames} value={r.source || ''} placeholder={t('property:cmp.sourceCol')} onChange={(v) => setRule(i, { source: v })} />}
                </div>
                <button className="text-[0.6875rem] text-blue-600 hover:underline shrink-0" onClick={() => setRule(i, useExpr ? { expr: undefined, source: r.source || '' } : { expr: r.expr || r.source || '', source: undefined })}>
                  {useExpr ? t('property:cmp.toColumn') : t('property:cmp.toExpr')}</button>
                <button className="text-gray-400 hover:text-red-500" onClick={() => patch({ rules: rules.filter((_, j) => j !== i) })}>✕</button>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[0.6875rem] text-gray-600">
                {(['trim', 'ignoreCase', 'numeric', 'date'] as const).map((o) => (
                  <label key={o} className="flex items-center gap-0.5"><input type="checkbox" checked={o === 'trim' ? r.trim !== false : !!r[o]} onChange={(e) => setRule(i, { [o]: e.target.checked })} />{t(`property:cmp.opt.${o}`)}</label>
                ))}
                {r.numeric && <label className="flex items-center gap-0.5">{t('property:cmp.opt.tolerance')}<input className={`${inp} w-14`} type="number" step="any" value={r.tolerance ?? 0} onChange={(e) => setRule(i, { tolerance: Number(e.target.value) || 0 })} /></label>}
                <span className={`ml-auto px-1 rounded ${r.origin === 'ai' ? 'bg-violet-100 text-violet-700' : r.origin === 'rule' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
                  title={r.note || ''}>{t(`property:cmp.origin.${r.origin || 'user'}`)}</span>
              </div>
              {r.note && <div className="text-[0.625rem] text-gray-400 mt-0.5">{r.note}</div>}
            </div>
          );
        })}
      </Section>

      <Section title={t('property:cmp.run')} hint={t('property:cmp.runHint')}>
        <div className="flex flex-col gap-0.5 text-xs text-gray-700 mb-1.5">
          <label className="flex items-center gap-1"><input type="radio" checked={(cfg.timing || 'ready') === 'ready'} onChange={() => patch({ timing: 'ready' })} />{t('property:cmp.atReady')}</label>
          <label className="flex items-center gap-1"><input type="radio" checked={cfg.timing === 'end'} onChange={() => patch({ timing: 'end' })} />{t('property:cmp.atEnd')}</label>
        </div>
        <div className="flex flex-col gap-0.5 text-xs text-gray-700">
          <label className="flex items-center gap-1"><input type="radio" checked={(cfg.compareMode || 'memory') === 'memory'} onChange={() => patch({ compareMode: 'memory' })} />{t('property:cmp.modeMemory')}</label>
          <label className="flex items-center gap-1"><input type="radio" checked={cfg.compareMode === 'db'} onChange={() => patch({ compareMode: 'db' })} />{t('property:cmp.modeDb')}</label>
        </div>
        {cfg.compareMode === 'db' && (
          <div className="mt-1 text-[0.6875rem] text-gray-500">
            {hasSam && (
              <div className="flex items-center gap-1 mb-0.5">{t('property:cmp.stageConn')}
                <select className={inp} value={cfg.stageConnection || ''} onChange={(e) => patch({ stageConnection: e.target.value })}>
                  <option value="">{t('property:cmp.stageAuto')}</option>
                  {connections.map((c: any) => <option key={c.connection_id} value={c.connection_id}>{c.connection_id}</option>)}
                </select>
              </div>
            )}
            {t('property:cmp.modeDbHint')}
          </div>
        )}
        <label className="mt-1.5 flex items-center gap-1 text-xs text-gray-700">{t('property:cmp.maxDetail')}
          <input className={`${inp} w-24`} type="number" min={1} value={cfg.maxDetail ?? 10000} onChange={(e) => patch({ maxDetail: Number(e.target.value) || 10000 })} /></label>
      </Section>

      <Section title={t('property:cmp.report')} hint={t('property:cmp.reportHint')}>
        {report && report.kind === 'sam'
          ? <div className="text-xs text-gray-700 font-mono break-all">{report.name}</div>
          : <div className="text-xs text-amber-600">{t('property:cmp.noReport')}</div>}
      </Section>
    </div>
  );
};

export default CompareNodePanel;
